import { useEffect } from "react";
import { Box, Button, Checkbox, Stack, Text } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { t } from "i18next";
import { UserNotificationChannel } from "../models/UserNotificationChannel";
import { useStores } from "../../shared/stores/RootStore";
import { toast } from "../../shared/views/CustomToaster";
import { CustomDialog } from "../../shared/views/CustomDialog";

const i18Prefix = "notification:notificationSettingsModal";

interface Props {
  onClose: () => void;
}
const NotificationSettingsModalBase: React.FC<Props> = ({ onClose }) => {
  const { notificationStore } = useStores();

  const isMainChecked = (channels: UserNotificationChannel[]) =>
    !channels.find((ch) => ch.enabled === false);

  useEffect(() => {
    notificationStore.getUserNotificationsSettings();
  }, [notificationStore]);

  const handleOnSubmit = () => {
    notificationStore
      .updateNotificationSettings()
      .then(() => {
        toast("success", t(`${i18Prefix}.success`));
        onClose();
      })
      .catch(() => {
        toast("error", t(`${i18Prefix}.error`));
      });
  };

  return (
    <CustomDialog
      size="xl"
      onClose={onClose}
      header={t(`${i18Prefix}.title`)}
      footer={
        <Button
          disabled={!notificationStore.settings.length}
          colorPalette="primary"
          onClick={handleOnSubmit}
        >
          {t(`${i18Prefix}.submit`)}
        </Button>
      }
    >
      <Stack gap={4} fontSize="sm">
        {notificationStore.settings.length ? (
          <Text>{t(`${i18Prefix}.info`)}</Text>
        ) : (
          <Text>{t(`${i18Prefix}.noNotificationsSettings`)}</Text>
        )}
        {notificationStore.settings.map((notification, i) => {
          return (
            <Box key={i}>
              <Checkbox.Root
                colorPalette="primary"
                checked={isMainChecked(notification.channels!)}
                onCheckedChange={(e: any) =>
                  notification.channels.forEach((channel) =>
                    notificationStore.updateUserNotificationListItem(
                      notification.groupKey!,
                      channel.type,
                      e.checked
                    )
                  )
                }
              >
                <Checkbox.HiddenInput />
                <Checkbox.Control />
                <Checkbox.Label>
                  <Text fontSize="sm">
                    {t(`${i18Prefix}.${notification.groupKey}`)}
                  </Text>
                </Checkbox.Label>
              </Checkbox.Root>
              <Stack pl={6} pt={2}>
                {notification.channels.map((channel, j) => (
                  <Checkbox.Root
                    key={j}
                    colorPalette="primary"
                    checked={channel.enabled}
                    onCheckedChange={(e: any) =>
                      notificationStore.updateUserNotificationListItem(
                        notification.groupKey!,
                        channel.type,
                        e.checked
                      )
                    }
                  >
                    <Checkbox.HiddenInput />
                    <Checkbox.Control />
                    <Checkbox.Label>
                      <Text fontSize="sm">
                        {t(`${i18Prefix}.${channel.type}`)}
                      </Text>
                    </Checkbox.Label>
                  </Checkbox.Root>
                ))}
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </CustomDialog>
  );
};

export const NotificationSettingsModal = observer(
  NotificationSettingsModalBase
);
